import React, {
  ChangeEvent,
  FormEvent,
  useContext,
  useEffect,
  useState,
} from 'react';
import { Link } from 'react-router-dom';
import { Button } from '../../../../../ui';
import { DatePicker } from '../../../../../ui/input/date_picker/date_picker';
import { TextArea } from '../../../../../ui/input/text_area/text_area';
import { TextInput } from '../../../../../ui/input/text_input/text_input';
import {
  createAssessment,
  getAssessment,
  updateAssessment,
} from '../../../../../utils/api_service/assessment_api';
import { AssessmentContext } from '../../../../../utils/contexts/contexts';
import {
  Assessment,
  BasicInfoInputs,
} from '../../../../../utils/types/assessment_types';

export const BasicInformation = () => {
  const { assessmentState, setAssessmentState } = useContext(AssessmentContext);
  const [basicInfoInputs, setBasicInfoInputs] = useState<BasicInfoInputs>({
    type: assessmentState.assessmentType, // e.g. "employee"
    title: '',
    description: '',
    jobPosition: '',
    startDate: new Date().toISOString().split('T')[0],
    endDate: new Date(Date.now() + 7 * 24 * 60 * 60 * 1000)
      .toISOString()
      .split('T')[0],
  });

  useEffect(() => {
    loadAssessment();
  }, []);

  const loadAssessment = () => {
    if (!assessmentState.assessmentId) return;
    getAssessment(assessmentState.assessmentId).then((response: Assessment) => {
      setBasicInfoInputs((prev) => ({
        ...prev,
        title: response.assessmentTitle,
        description: response.description,
        startDate: new Date(response.startDate * 1000)
          .toISOString()
          .split('T')[0],
        endDate: new Date(response.endDate * 1000).toISOString().split('T')[0],
        jobPosition: response.jobPosition || '',
      }));
    });
  };

  const handleChange = (
    event: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
  ) => {
    const { name, value } = event.target;
    setBasicInfoInputs((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const changeDate = (timeRange: {
    startDate: string | null;
    endDate: string | null;
  }) => {
    setBasicInfoInputs((prev) => ({
      ...prev,
      startDate: timeRange.startDate || '',
      endDate: timeRange.endDate || '',
    }));
  };

  const handleSubmit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();

    // send the raw slug in `type` (e.g. "employee")
    const payload: BasicInfoInputs = { ...basicInfoInputs };

    if (assessmentState.assessmentId) {
      updateAssessment(payload, assessmentState.assessmentId).then(() => {
        setAssessmentState({
          ...assessmentState,
          assessmentPageNo: 2,
        });
      });
    } else {
      createAssessment(payload).then((response) => {
        setAssessmentState({
          ...assessmentState,
          assessmentId: response.id,
          assessmentPageNo: 2,
        });
      });
    }
  };

  return (
    <div className='w-full h-full flex flex-col gap-5'>
      <nav
          className='flex w-full justify-between items-center border-b py-3 text-sm'
          aria-label='Breadcrumb'
      >
        <ol className='inline-flex items-center space-x-1 md:space-x-2'>
          <li>
            <Link to={'#'} className='text-primary-textGray hover:underline'>
              Basic Information
            </Link>
          </li>
          <li>
            <span className='text-gray-400'>/</span>
          </li>
          <li>
            <Link to={'#'} className='text-primary-textGray hover:underline'>
              Create a new assessment and choose questions in the next step
            </Link>
          </li>
        </ol>
      </nav>

      <form onSubmit={handleSubmit} className='flex flex-col gap-5 max-w-2xl'>
        <TextInput
            label='Assessment Name'
            name='title'
            value={basicInfoInputs.title}
            type='text'
            onChange={handleChange}
        />

        { /* ALWAYS render Job Position */ }
        <TextInput
            label='Job Position'
            name='jobPosition'
            value={basicInfoInputs.jobPosition || ''}
            type='text'
            onChange={handleChange}
        />

        <DatePicker
            label='Time-Range'
            startDate={basicInfoInputs.startDate}
            endDate={basicInfoInputs.endDate}
            onChange={changeDate}
        />

        <TextArea
            label='Description'
            name='description'
            value={basicInfoInputs.description}
            onChange={handleChange}
        />

        <Button
            type='submit'
            onClick={() => {}}
            variant={'primary'}
            label={'Next'}
            width='120px'
        />
      </form>
    </div>
  );
};
